import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import {
  CalendarDaysIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  PlayCircleIcon,
} from "@heroicons/react/24/outline";
import { Card } from "@/components/ui/Card";
import { Spinner } from "@/components/ui/Spinner";
import {
  addDaysIso,
  formatAbsenceDateRange,
  formatAbsenceDays,
  formatBudgetDays,
  getAbsenceTypeLabel,
  isUnavailabilityNotice,
  listAbsenceRequests,
  listHeroTeamAbsences,
  type EmployeeAbsenceRequest,
} from "@/lib/employeeAbsences";
import { listUnavailableOnDay, type UnavailableOnDayPerson } from "@/lib/montageplanung";
import {
  formatDuration,
  formatHours,
  listSubcontractorEntries,
  type EmployeeTimeEntry,
  type EmployeeTimeTimer,
} from "@/lib/employeeTimes";
import { useRealtimeSync } from "@/lib/useRealtime";
import {
  dedupeAbsencesById,
  type PersonalSelfData,
} from "@/pages/personal/hub/usePersonalSelfData";
import { splitTeamAbsences, type TeamTodayEntry } from "@/pages/personal/hub/teamAbsenceRows";

const OVERVIEW_LINK_CLASS =
  "inline-flex items-center rounded-full border border-accent px-3 py-1 text-xs font-medium text-accent hover:bg-accent/10";

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

function isActiveOn(req: EmployeeAbsenceRequest, today: string): boolean {
  const from = req.date_from.slice(0, 10);
  const to = req.date_to.slice(0, 10);
  return from <= today && to >= today;
}

function absenceTone(req: EmployeeAbsenceRequest): string {
  if (req.status === "pending") return "!bg-status-orange/15 !text-status-orange";
  switch (req.absence_type) {
    case "krank":
    case "kindkrank":
      return "!bg-status-red/15 !text-status-red";
    case "urlaub":
    case "sonderurlaub":
      return "!bg-status-green/15 !text-status-green";
    default:
      return "!bg-accent/15 !text-accent";
  }
}

// ---------------------------------------------------------------------------
// KPI-Leiste
// ---------------------------------------------------------------------------

interface MetricItem {
  key: string;
  label: string;
  value: string;
  hint?: string;
  href?: string;
  title?: string;
  tone?: "default" | "alert" | "good";
}

export function PersonalKpiStrip({
  data,
  loading,
  extra = [],
}: {
  data: PersonalSelfData;
  loading: boolean;
  extra?: MetricItem[];
}) {
  const year = new Date().getFullYear();
  const items = useMemo((): MetricItem[] => {
    const list: MetricItem[] = [];
    const { account, month, absences } = data;
    const pending = loading ? null : account;

    if (!account || account.tracks_overtime !== false) {
      const value =
        pending?.overtime_balance_hours != null
          ? formatHours(pending.overtime_balance_hours)
          : loading
            ? "…"
            : "—";
      list.push({
        key: "overtime",
        label: "Stundenkonto",
        value,
        hint: account?.overtime_excludes_today ? "ohne heute" : undefined,
        href: "/personal/me?tab=year",
        title: "Aktueller Überstundenstand",
        tone:
          pending?.overtime_balance_hours != null && pending.overtime_balance_hours < 0
            ? "alert"
            : "default",
      });
    }

    const available =
      account?.available_vacation_days ?? account?.remaining_vacation_days ?? null;
    list.push({
      key: "vacation",
      label: `Urlaub ${year}`,
      value: available != null ? formatBudgetDays(available) : loading ? "…" : "—",
      hint: available != null ? "Tage verfügbar" : undefined,
      href: "/personal/me?tab=absence",
      title: "Noch verfügbarer Urlaub inkl. offener Anträge",
    });

    const monthLabel = new Date().toLocaleDateString("de-DE", { month: "long" });
    const hasSoll = month?.has_soll !== false && month?.expected_hours != null;
    list.push({
      key: "month",
      label: `Stunden ${monthLabel}`,
      value: month ? formatHours(month.total_hours) : loading ? "…" : "—",
      hint: hasSoll ? `Soll ${formatHours(month?.expected_hours)}` : undefined,
      href: "/personal/me?tab=month",
      title: "Erfasste Stunden im laufenden Monat",
    });

    const ownPending = absences.filter((a) => a.status === "pending").length;
    list.push({
      key: "pending",
      label: "Meine offenen Anträge",
      value: loading ? "…" : String(ownPending),
      hint: ownPending > 0 ? "warten auf Freigabe" : "alles freigegeben",
      href: "/personal/me?tab=absence",
      tone: ownPending > 0 ? "alert" : "good",
    });

    return [...list, ...extra];
  }, [data, loading, extra, year]);

  return (
    <div className="eh-home-dashboard-strip__metrics">
      {items.map((item) => (
        <Metric key={item.key} item={item} />
      ))}
    </div>
  );
}

function Metric({ item }: { item: MetricItem }) {
  const body = (
    <>
      <span className="eh-home-metric__label">{item.label}</span>
      <strong
        className={clsx(
          "eh-home-metric__value",
          item.tone === "alert" && "text-status-red",
          item.tone === "good" && "text-status-green",
        )}
      >
        {item.value}
      </strong>
      {item.hint ? (
        <span className="text-[11px] leading-tight text-label-tertiary">{item.hint}</span>
      ) : null}
    </>
  );
  if (!item.href) {
    return (
      <div className="eh-home-metric" title={item.title}>
        {body}
      </div>
    );
  }
  return (
    <Link className="eh-home-metric" to={item.href} title={item.title}>
      {body}
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Laufender Timer
// ---------------------------------------------------------------------------

export function ActiveTimerBanner({ timer }: { timer: EmployeeTimeTimer | null }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!timer) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [timer]);

  if (!timer) return null;

  const startedMs = new Date(timer.started_at).getTime();
  const elapsed = Number.isFinite(startedMs)
    ? Math.max(0, Math.floor((now - startedMs) / 1000))
    : Math.max(0, timer.elapsed_seconds ?? 0);
  const project = [timer.auftragsnummer, timer.kunde].filter(Boolean).join(" · ");
  const startedAt = Number.isFinite(startedMs)
    ? new Date(startedMs).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
    : null;

  return (
    <Link
      to="/personal/me"
      className="group flex flex-wrap items-center gap-3 rounded-ios border border-status-green/40 bg-status-green/10 px-4 py-3 shadow-ios transition hover:border-status-green hover:bg-status-green/15"
    >
      <span className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-status-green/20 text-status-green">
        <PlayCircleIcon className="h-6 w-6" aria-hidden />
        <span
          className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 animate-pulse rounded-full bg-status-green"
          aria-hidden
        />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-xs font-semibold uppercase tracking-wide text-status-green">
          Timer läuft{startedAt ? ` · seit ${startedAt} Uhr` : ""}
        </span>
        <span className="block truncate text-sm font-medium text-label">
          {project || timer.beschreibung || "Ohne Projektzuordnung"}
        </span>
        {timer.comment ? (
          <span className="block truncate text-xs text-label-secondary">{timer.comment}</span>
        ) : null}
      </span>
      <span className="text-xl font-bold tabular-nums text-label">{formatDuration(elapsed)}</span>
      <ChevronRightIcon
        className="h-5 w-5 shrink-0 text-label-tertiary transition group-hover:text-status-green"
        aria-hidden
      />
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Eigene kommende Abwesenheiten
// ---------------------------------------------------------------------------

const UPCOMING_LIMIT = 5;

export function UpcomingAbsencesCard({
  absences,
  loading,
}: {
  absences: EmployeeAbsenceRequest[];
  loading: boolean;
}) {
  const today = todayIso();
  const upcoming = useMemo(
    () =>
      absences
        .filter((a) => {
          if (a.date_to.slice(0, 10) < today) return false;
          if (isUnavailabilityNotice(a.absence_type)) {
            return a.status === "pending" || a.status === "acknowledged";
          }
          return a.status === "approved" || a.status === "pending";
        })
        .sort((a, b) => a.date_from.localeCompare(b.date_from)),
    [absences, today],
  );
  const shown = upcoming.slice(0, UPCOMING_LIMIT);
  const hidden = upcoming.length - shown.length;

  return (
    <Card
      title="Meine Abwesenheiten"
      description="Genehmigte und beantragte Abwesenheiten ab heute."
      actions={
        <Link to="/personal/me?tab=absence" className={OVERVIEW_LINK_CLASS}>
          Abwesenheit beantragen
        </Link>
      }
      bodyClassName="!p-0"
    >
      {loading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : upcoming.length === 0 ? (
        <div className="eh-home-queue">
          <div className="eh-home-empty" role="status">
            <span className="eh-home-empty__mark" aria-hidden="true">
              <CalendarDaysIcon />
            </span>
            <span>
              <span className="eh-home-empty__title">Keine Abwesenheiten geplant</span>
              <span className="eh-home-empty__meta">
                Urlaub oder Überstundenabbau können Sie in „Mein Bereich“ beantragen.
              </span>
            </span>
          </div>
        </div>
      ) : (
        <ul className="eh-home-queue eh-home-queue-scroll" role="list">
          {shown.map((req) => {
            const active = isActiveOn(req, today);
            return (
              <li key={req.id}>
                <Link to="/personal/me?tab=absence" className="eh-home-queue-row">
                  <span className="eh-home-queue-row__kicker">
                    {active ? "heute" : formatShortDate(req.date_from)}
                  </span>
                  <span className="eh-home-queue-row__copy">
                    <span className="eh-home-queue-row__title">
                      {getAbsenceTypeLabel(req.absence_type)}
                    </span>
                    <span className="eh-home-queue-row__meta">
                      {formatAbsenceDateRange(req)} · {formatAbsenceDays(req.total_days)}
                    </span>
                  </span>
                  <span
                    className={clsx(
                      "eh-home-queue-row__status eh-home-queue-row__status--show",
                      isUnavailabilityNotice(req.absence_type)
                        ? "!bg-fill-tertiary !text-label-secondary"
                        : req.status === "pending"
                          ? "!bg-status-orange/15 !text-status-orange"
                          : "!bg-status-green/15 !text-status-green",
                    )}
                  >
                    {isUnavailabilityNotice(req.absence_type)
                      ? req.status === "acknowledged"
                        ? "Gelesen"
                        : "Hinweis"
                      : req.status === "pending"
                        ? "Ausstehend"
                        : "Genehmigt"}
                  </span>
                  <ChevronRightIcon className="eh-home-queue-row__go" aria-hidden="true" />
                </Link>
              </li>
            );
          })}
          {hidden > 0 ? (
            <li className="px-2 pt-1 text-xs text-label-secondary">
              {hidden === 1 ? "1 weitere Abwesenheit" : `${hidden} weitere Abwesenheiten`}
            </li>
          ) : null}
        </ul>
      )}
    </Card>
  );
}

function formatShortDate(iso: string): string {
  const [, m, d] = iso.slice(0, 10).split("-");
  return m && d ? `${d}.${m}.` : iso;
}

// ---------------------------------------------------------------------------
// Team: heute abwesend / kommende Woche
// ---------------------------------------------------------------------------

const TEAM_UPCOMING_DAYS = 7;

export function TeamAbsencesCard({ enabled }: { enabled: boolean }) {
  const [items, setItems] = useState<EmployeeAbsenceRequest[]>([]);
  const [unavailableToday, setUnavailableToday] = useState<UnavailableOnDayPerson[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!enabled) {
        setLoading(false);
        return;
      }
      if (!opts?.silent) setLoading(true);
      try {
        const today = todayIso();
        const horizon = addDaysIso(today, TEAM_UPCOMING_DAYS);
        const year = new Date().getFullYear();
        const month = new Date().getMonth() + 1;
        const years = month >= 12 ? [year, year + 1] : [year];
        const [lists, heroRows, unavailable] = await Promise.all([
          Promise.all(years.map((y) => listAbsenceRequests({ year: y }))),
          listHeroTeamAbsences({ date_from: today, date_to: horizon }).catch(
            () => [] as EmployeeAbsenceRequest[],
          ),
          listUnavailableOnDay(today).catch(() => [] as UnavailableOnDayPerson[]),
        ]);
        setItems(dedupeAbsencesById([...lists.flat(), ...heroRows]));
        setUnavailableToday(Array.isArray(unavailable) ? unavailable : []);
        setError(null);
      } catch {
        setError("Abwesenheiten konnten nicht geladen werden.");
      } finally {
        setLoading(false);
      }
    },
    [enabled],
  );

  useEffect(() => {
    void load();
  }, [load]);

  useRealtimeSync({
    domains: ["employee-times", "hero", "montageplanung"],
    enabled,
    onChange: () => void load({ silent: true }),
  });

  const today = todayIso();
  const horizon = addDaysIso(today, TEAM_UPCOMING_DAYS);

  const { todayRows, upcomingRows } = useMemo(
    () => splitTeamAbsences({ absences: items, unavailableToday, today, horizon }),
    [items, unavailableToday, today, horizon],
  );

  if (!enabled) return null;

  return (
    <Card
      title={
        <span className="inline-flex flex-wrap items-center gap-2">
          Heute abwesend
          {!loading && todayRows.length > 0 ? (
            <span className="rounded-full bg-accent/15 px-2 py-0.5 text-xs font-semibold text-accent">
              {todayRows.length}
            </span>
          ) : null}
        </span>
      }
      description={`Team-Übersicht inkl. der nächsten ${TEAM_UPCOMING_DAYS} Tage.`}
      actions={
        <Link to="/zeiterfassung" className={OVERVIEW_LINK_CLASS}>
          Alle Abwesenheiten
        </Link>
      }
      bodyClassName="!p-0"
    >
      {loading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : error ? (
        <p className="px-4 py-6 text-sm text-status-red">{error}</p>
      ) : (
        <div className="eh-home-queue eh-home-queue-scroll">
          {todayRows.length === 0 ? (
            <div className="eh-home-empty" role="status">
              <span className="eh-home-empty__mark" aria-hidden="true">
                <CheckCircleIcon />
              </span>
              <span>
                <span className="eh-home-empty__title">Heute ist niemand abwesend</span>
                <span className="eh-home-empty__meta">Das gesamte Team ist im Dienst.</span>
              </span>
            </div>
          ) : (
            <ul className="flex flex-col gap-1.5" role="list">
              {todayRows.map((entry) => (
                <li key={todayEntryKey(entry)}>
                  {entry.source === "absence" ? (
                    <TeamAbsenceRow req={entry.req} />
                  ) : (
                    <TeamUnavailableRow person={entry.person} />
                  )}
                </li>
              ))}
            </ul>
          )}

          {upcomingRows.length > 0 ? (
            <>
              <div className="mt-2 px-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-label-tertiary">
                Demnächst
              </div>
              <ul className="flex flex-col gap-1.5" role="list">
                {upcomingRows.map((req) => (
                  <li key={req.id}>
                    <TeamAbsenceRow req={req} showDate />
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
      )}
    </Card>
  );
}

function todayEntryKey(entry: TeamTodayEntry): string {
  return entry.source === "absence" ? `absence-${entry.req.id}` : `unavail-${entry.person.id}`;
}

function TeamUnavailableRow({ person }: { person: UnavailableOnDayPerson }) {
  const name = person.user_display || person.user_login;
  const from = person.date_from.slice(0, 10);
  const to = person.date_to.slice(0, 10);
  const note = person.note?.trim();
  const when = from === to ? "nur heute" : `bis ${formatShortDateYear(to)}`;
  return (
    <div className="eh-home-queue-row cursor-default hover:!border-transparent hover:!bg-[color-mix(in_srgb,var(--eh-fill-quaternary)_70%,transparent)]">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent/15 text-xs font-bold uppercase text-accent">
        {initials(name)}
      </span>
      <span className="eh-home-queue-row__copy">
        <span className="eh-home-queue-row__title">{name}</span>
        <span className="eh-home-queue-row__meta">{note ? `${when} · ${note}` : when}</span>
      </span>
      <span className="eh-home-queue-row__status eh-home-queue-row__status--show !bg-fill-secondary !text-label-secondary">
        Nicht verfügbar
      </span>
    </div>
  );
}

function TeamAbsenceRow({ req, showDate = false }: { req: EmployeeAbsenceRequest; showDate?: boolean }) {
  const name = req.user_display || req.user_login;
  const range = formatAbsenceDateRange(req);
  const isSingleDay = req.date_from === req.date_to;
  const meta = showDate
    ? `${range}${isSingleDay ? "" : ` · ${formatAbsenceDays(req.total_days)}`}`
    : isSingleDay
      ? "nur heute"
      : `bis ${formatShortDateYear(req.date_to)}`;
  return (
    <div className="eh-home-queue-row cursor-default hover:!border-transparent hover:!bg-[color-mix(in_srgb,var(--eh-fill-quaternary)_70%,transparent)]">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent/15 text-xs font-bold uppercase text-accent">
        {initials(name)}
      </span>
      <span className="eh-home-queue-row__copy">
        <span className="eh-home-queue-row__title">{name}</span>
        <span className="eh-home-queue-row__meta">{meta}</span>
      </span>
      <span
        className={clsx(
          "eh-home-queue-row__status eh-home-queue-row__status--show",
          absenceTone(req),
        )}
      >
        {getAbsenceTypeLabel(req.absence_type)}
        {req.status === "pending" ? " (beantragt)" : ""}
      </span>
    </div>
  );
}

function formatShortDateYear(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split("-");
  return y && m && d ? `${d}.${m}.${y}` : iso;
}

function initials(name: string): string {
  const parts = name
    .replace(/[(),]/g, " ")
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2);
  return `${parts[0]![0] ?? ""}${parts[parts.length - 1]![0] ?? ""}`;
}

// ---------------------------------------------------------------------------
// Nachunternehmer: offene Zeiten
// ---------------------------------------------------------------------------

interface SubcontractorAggregate {
  login: string;
  display: string;
  hours: number;
  count: number;
  oldest: string;
}

export function SubcontractorOpenCard({ enabled }: { enabled: boolean }) {
  const [entries, setEntries] = useState<EmployeeTimeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!enabled) {
        setLoading(false);
        return;
      }
      if (!opts?.silent) setLoading(true);
      try {
        const rows = await listSubcontractorEntries({ paid: "open" });
        setEntries(Array.isArray(rows) ? rows : []);
        setError(null);
      } catch {
        setError("Nachunternehmer-Zeiten konnten nicht geladen werden.");
      } finally {
        setLoading(false);
      }
    },
    [enabled],
  );

  useEffect(() => {
    void load();
  }, [load]);

  useRealtimeSync({
    domains: ["employee-times"],
    enabled,
    onChange: () => void load({ silent: true }),
  });

  const { rows, totalHours } = useMemo(() => {
    const byLogin = new Map<string, SubcontractorAggregate>();
    let totalHours = 0;
    for (const e of entries) {
      const hours = Number(e.hours_decimal) || 0;
      totalHours += hours;
      const key = e.user_login;
      const agg = byLogin.get(key) ?? {
        login: key,
        display: e.user_display || key,
        hours: 0,
        count: 0,
        oldest: e.work_date,
      };
      agg.hours += hours;
      agg.count += 1;
      if (e.work_date < agg.oldest) agg.oldest = e.work_date;
      byLogin.set(key, agg);
    }
    const rows = Array.from(byLogin.values()).sort((a, b) => b.hours - a.hours);
    return { rows, totalHours };
  }, [entries]);

  if (!enabled) return null;

  return (
    <Card
      title={
        <span className="inline-flex flex-wrap items-center gap-2">
          Nachunternehmer – offene Zeiten
          {!loading && entries.length > 0 ? (
            <span className="rounded-full bg-status-orange/15 px-2 py-0.5 text-xs font-semibold text-status-orange">
              {formatHours(totalHours)}
            </span>
          ) : null}
        </span>
      }
      description="Noch nicht als bezahlt markierte Zeiteinträge."
      actions={
        <Link to="/personal/nachunternehmer" className={OVERVIEW_LINK_CLASS}>
          Zur Abrechnung
        </Link>
      }
      bodyClassName="!p-0"
    >
      {loading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : error ? (
        <p className="px-4 py-6 text-sm text-status-red">{error}</p>
      ) : rows.length === 0 ? (
        <div className="eh-home-queue">
          <div className="eh-home-empty" role="status">
            <span className="eh-home-empty__mark" aria-hidden="true">
              <CheckCircleIcon />
            </span>
            <span>
              <span className="eh-home-empty__title">Keine offenen Zeiten</span>
              <span className="eh-home-empty__meta">
                Alle Nachunternehmer-Einträge sind abgerechnet.
              </span>
            </span>
          </div>
        </div>
      ) : (
        <ul className="eh-home-queue eh-home-queue-scroll" role="list">
          {rows.map((row) => (
            <li key={row.login}>
              <Link to="/personal/nachunternehmer" className="eh-home-queue-row">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent/15 text-xs font-bold uppercase text-accent">
                  {initials(row.display)}
                </span>
                <span className="eh-home-queue-row__copy">
                  <span className="eh-home-queue-row__title">{row.display}</span>
                  <span className="eh-home-queue-row__meta">
                    {row.count === 1 ? "1 Eintrag" : `${row.count} Einträge`} · ältester{" "}
                    {formatShortDateYear(row.oldest)}
                  </span>
                </span>
                <span className="eh-home-queue-row__status eh-home-queue-row__status--show !bg-status-orange/15 !text-status-orange tabular-nums">
                  {formatHours(row.hours)}
                </span>
                <ChevronRightIcon className="eh-home-queue-row__go" aria-hidden="true" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
